import React, { useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import { usePutOrganization } from 'api';
import { getBankAccountCurrencies } from 'utility/banks';

import ToastMessage from 'components/toastMessage';
import { Box, Button } from 'components/ui';
import { BankAccountFormModal, DeleteConfirmationModal, ListItem } from './components';

import * as Styled from './SettingsBanking.styles';

export const SettingsBanking = ({ organizationData, refetchOrganizationData }) => {
  const [editBankAccountData, setEditBankAccountData] = useState(null);
  const [deleteBankAccountData, setDeleteBankAccountData] = useState(null);
  const [toastData, setToastData] = useState(null);

  const { mutate: updateOrganization, isLoading: isUpdatingOrganizationData } = usePutOrganization({
    onSuccess: () => {
      refetchOrganizationData();
      setEditBankAccountData(null);
      setDeleteBankAccountData(null);
      setToastData({ severity: 'success', message: 'Zapisano ustawienia' });
    },
    onError: () => {
      setToastData({ severity: 'error', message: 'Nie udało się zapisać ustawień' });
    },
  });

  const bankAccounts = useMemo(() => {
    return get(organizationData, 'organization.bankAccounts', []);
  }, [organizationData]);

  const updateBankAccounts = (oldBankAccountData, newBankAccountData) => {
    const newBankAccounts = sortBy(
      [
        ...bankAccounts.filter(
          (bankAccount) =>
            bankAccount.accountNumber !== newBankAccountData.accountNumber &&
            bankAccount.accountNumber !== oldBankAccountData.accountNumber,
        ),
        newBankAccountData,
      ],
      ['name'],
    );

    updateOrganization({
      bankAccounts: newBankAccounts,
    });
  };

  const deleteBankAccount = (bankAccountData) => {
    updateOrganization({
      bankAccounts: bankAccounts.filter((bankAccount) => bankAccount.accountNumber !== bankAccountData.accountNumber),
    });
  };

  const getBankAccountListItems = (currency) => {
    return bankAccounts
      .filter((bankAccount) => bankAccount.currency === currency)
      .map((bankAccount) => (
        <ListItem
          key={bankAccount.accountNumber}
          item={bankAccount}
          onEdit={() => setEditBankAccountData(bankAccount)}
          onDelete={() => setDeleteBankAccountData(bankAccount)}
        />
      ));
  };

  const getBankAccountCurrenciesLists = () => {
    const configuredCurrences = bankAccounts.map((bankAccount) => bankAccount.currency);
    return getBankAccountCurrencies()
      .filter((currency) => configuredCurrences.includes(currency.label))
      .map((currency) => (
        <React.Fragment>
          <Styled.Label>{currency.label}</Styled.Label>
          <Styled.List>{getBankAccountListItems(currency.value)}</Styled.List>
        </React.Fragment>
      ));
  };

  const getBankAccountNumbers = () => {
    return bankAccounts
      .filter((bankAccount) => bankAccount.accountNumber !== editBankAccountData.accountNumber)
      .map((bankAccount) => bankAccount.accountNumber);
  };

  return (
    <Box title="Rachunki bankowe">
      <Button onClick={() => setEditBankAccountData({})}>Dodaj rachunek</Button>
      {getBankAccountCurrenciesLists()}

      {toastData && (
        <ToastMessage
          open
          onClose={() => setToastData(null)}
          severity={toastData.severity}
          message={toastData.message}
        />
      )}

      {editBankAccountData && (
        <BankAccountFormModal
          data={editBankAccountData}
          onClose={() => setEditBankAccountData(null)}
          onConfirm={(data) => updateBankAccounts(editBankAccountData, data)}
          onDelete={() => setDeleteBankAccountData(editBankAccountData)}
          isSaving={isUpdatingOrganizationData}
          getBankAccountNumbers={() => getBankAccountNumbers()}
        />
      )}

      {deleteBankAccountData && (
        <DeleteConfirmationModal
          onClose={() => setDeleteBankAccountData(null)}
          onConfirm={() => deleteBankAccount(deleteBankAccountData)}
          isSaving={isUpdatingOrganizationData}
        />
      )}
    </Box>
  );
};

SettingsBanking.propTypes = {
  organizationData: PropTypes.object.isRequired,
  refetchOrganizationData: PropTypes.func.isRequired,
};
